import type React from "react";
import { NightDecor } from "@/components/blocks/decor/Decor";
import { MaskText } from "@/components/motion/MaskText";
import { cn } from "@/lib/utils";
import { Overline } from "./SectionHeading";

export const displayH1 =
	"text-[clamp(2.2rem,4.8vw,4.4rem)] leading-[1.04] font-semibold tracking-[-0.055em] text-balance";

/**
 * Night hero shared by every page: overline, masked title with its italic
 * emphasis, intro, actions, and an optional product scene on the right.
 * Text is animated in CSS so it paints before hydration.
 */
export function PageHero({
	id,
	label,
	title,
	emphasis,
	intro,
	children,
	visual,
	note,
	footer,
	className,
}: {
	id?: string;
	label: string;
	title: string;
	emphasis?: string;
	intro: string;
	/** Actions (CTAs). */
	children?: React.ReactNode;
	/** Product scene shown next to the text on large screens. */
	visual?: React.ReactNode;
	/** Small print under the actions. */
	note?: React.ReactNode;
	/** Full-width band under the hero (marquee, facts). */
	footer?: React.ReactNode;
	className?: string;
}) {
	return (
		<section
			id={id}
			className={cn(
				"dark relative isolate scroll-mt-16 overflow-clip bg-background text-foreground",
				className,
			)}
		>
			<div aria-hidden="true" className="absolute inset-0 -z-10">
				<NightDecor />
			</div>
			<div
				className={cn(
					"mx-auto grid w-full max-w-7xl gap-14 px-6 pt-16 pb-20 md:pt-24 md:pb-28 lg:px-10",
					visual &&
						"lg:grid-cols-[minmax(0,1.08fr)_minmax(0,1fr)] lg:items-center lg:gap-14",
				)}
			>
				<div className={cn("space-y-8", !visual && "max-w-4xl")}>
					<Overline className="fade-rise">{label}</Overline>
					<h1
						className={cn(
							displayH1,
							visual && "lg:text-[clamp(2.1rem,3.6vw,3.6rem)]",
						)}
					>
						<MaskText text={title} emphasis={emphasis} delay={0.1} caret />
					</h1>
					<p
						className="fade-rise max-w-2xl text-lg text-pretty text-muted-foreground md:text-xl"
						style={{ "--delay": "0.45s" } as React.CSSProperties}
					>
						{intro}
					</p>
					{children ? (
						<div
							className="fade-rise flex flex-col gap-3 sm:flex-row sm:flex-wrap"
							style={{ "--delay": "0.6s" } as React.CSSProperties}
						>
							{children}
						</div>
					) : null}
					{note ? (
						<div
							className="fade-rise text-sm text-muted-foreground"
							style={{ "--delay": "0.75s" } as React.CSSProperties}
						>
							{note}
						</div>
					) : null}
				</div>
				{visual ? (
					<div
						className="fade-rise relative"
						style={{ "--delay": "0.35s" } as React.CSSProperties}
					>
						{visual}
					</div>
				) : null}
			</div>
			{footer}
		</section>
	);
}
